import { TextField } from '@mui/material'
import { getGridStringOperators, GridFilterInputValueProps, useGridApiContext } from '@mui/x-data-grid'
import isNil from 'lodash/isNil'
import { SyntheticEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseAutocomplete } from '@omnea-app/omui/BaseAutocomplete'

interface AutoCompleteInputFilterProps extends Omit<GridFilterInputValueProps, 'apiRef'> {
  valueProp?: string // Optional prop to use as value for options
}

const autocompletableOperators = new Set(['contains', 'doesNotContain', 'equals', 'doesNotEqual'])

export const getGridAutocompleteFilterOperator = (valueProp?: string) => [
  ...getGridStringOperators().map((operator) => {
    return autocompletableOperators.has(operator.value)
      ? {
          ...operator,
          InputComponent: (props: GridFilterInputValueProps) => <AutoCompleteInputFilter {...props} valueProp={valueProp} />,
        }
      : operator
  }),
]

export const AutoCompleteInputFilter = ({ item, applyValue, valueProp }: AutoCompleteInputFilterProps) => {
  const { t } = useTranslation('shared')
  const [inputValue, setInputValue] = useState(item.value ?? '')

  const apiRef = useGridApiContext()

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue)
  }

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setInputValue(newValue)
    applyValue({ ...item, value: newValue })
  }

  const handleBlur = () => {
    if (inputValue === item.value) return
    applyValue({ ...item, value: inputValue })
  }

  const options = useMemo(() => {
    const rowValues = [...apiRef.current.getRowModels()].map((values) => values[1][item.field]).filter((value) => !isNil(value))

    // if rowValues are arrays, flatten them, then check for valueProp
    const options = rowValues
      .flatMap((row) =>
        Array.isArray(row) ? row.map((item) => (!!valueProp ? item[valueProp] : item)) : !!valueProp ? row[valueProp] : row
      )
      .filter((value) => !!value)

    return [...new Set(options)]
  }, [item.field, apiRef, valueProp])

  return (
    <BaseAutocomplete
      freeSolo
      autoHighlight
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disableCloseOnSelect
      disableClearable
      onChange={handleChange}
      onBlur={handleBlur}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label={t('table-filters.filter-value-placeholder')}
          placeholder={t('table-filters.filter-value')}
          slotProps={{
            input: { ...params.InputProps, sx: { border: 'border.brand.strong' } },
            inputLabel: { shrink: true },
          }}
        />
      )}
    />
  )
}
